import { HiExternalLink, HiCalendar, HiMail, HiPhone, HiCheckCircle } from 'react-icons/hi';
import Section from '../components/ui/Section';
import HeroSection from '../components/ui/HeroSection';
import ChalkboardBackground from '../components/ui/ChalkboardBackground';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import siteContent from '../data/siteContent.json';
import homeBg from '../assets/images/bb-background.png';
import Seo from '../components/Seo';

export default function Booking() {
  return (
    <>
      <Seo
        title="Book Your Wedding Date"
        description="Check availability and book Blush & Bobby for your Kansas City wedding hair and makeup. Secure your 2026 bridal date today."
        path="/book"
      />
      {/* Hero */}
      <HeroSection title="Book Your Date" />

      {/* Main CTA - Linktree */}
      <ChalkboardBackground className="py-20">
        <div className="container-custom">
          <div className="max-w-2xl mx-auto text-center">
            <Card className="p-8 md:p-12">
              <div className="inline-flex items-center justify-center w-16 h-16 border border-dusty-rose/30 rounded-full mb-6">
                <HiCalendar className="h-8 w-8 text-dusty-rose" />
              </div>
              <h2 className="font-display text-2xl md:text-3xl font-semibold mb-4 text-chalk">
                Ready to Inquire?
              </h2>
              <p className="text-dusty-rose/80 mb-6 leading-relaxed">
                Click below to access our booking form and check availability for your wedding date.
                I typically respond within 24-48 hours!
              </p>
              <Button
                href={siteContent.social.linktree}
                target="_blank"
                rel="noopener noreferrer"
                variant="outline"
                size="lg"
              >
                Contact & Book Now
                <HiExternalLink className="ml-2 h-5 w-5" />
              </Button>
              <p className="text-sm text-dusty-rose/50 mt-4">
                Opens in a new tab
              </p>
            </Card>
          </div>
        </div>
      </ChalkboardBackground>

      {/* What to Include */}
      <Section background="charcoal" className="border-y border-dusty-rose/10">
        <div className="max-w-2xl mx-auto">
          <h2 className="font-display text-2xl font-semibold mb-6 text-center text-chalk">
            What to Include in Your Inquiry
          </h2>
          <Card className="p-6">
            <ul className="space-y-3">
              {[
                'Your wedding date and venue',
                'Services needed (hair, makeup, or both)',
                'Number of people in your bridal party',
                'Any specific styles or looks you have in mind',
                'How you heard about Blush & Bobby'
              ].map((item, index) => (
                <li key={index} className="flex items-start gap-3">
                  <HiCheckCircle className="h-5 w-5 text-dusty-rose flex-shrink-0 mt-0.5" />
                  <span className="text-dusty-rose/80">{item}</span>
                </li>
              ))}
            </ul>
          </Card>
        </div>
      </Section>

      {/* Policies */}
      <ChalkboardBackground className="py-20">
        <div className="container-custom">
          <div className="max-w-2xl mx-auto">
            <h2 className="font-display text-2xl font-semibold mb-6 text-center text-chalk">
              Booking Policies
            </h2>
            <div className="bg-charcoal-dark/40 border border-dusty-rose/20 p-6 rounded-2xl">
              <ul className="space-y-3 text-sm text-dusty-rose/80">
                <li>
                  <strong className="text-chalk">Retainer:</strong> A $150 non-refundable retainer is required to secure your date.
                </li>
                <li>
                  <strong className="text-chalk">Travel:</strong> Travel fees may apply for venues outside the KC metro area.
                </li>
                <li>
                  <strong className="text-chalk">Party Minimums:</strong> Minimum party sizes may apply for certain dates.
                </li>
                <li>
                  <strong className="text-chalk">Cancellation:</strong> Please review our full contract for cancellation terms.
                </li>
              </ul>
            </div>
          </div>
        </div>
      </ChalkboardBackground>

      {/* Alternative Contact */}
      <Section background="charcoal" className="border-t border-dusty-rose/10">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="font-display text-xl font-semibold mb-4 text-chalk">
            Prefer Email?
          </h2>
          <p className="text-dusty-rose/70 mb-6">
            You can also reach out directly via email or phone.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href={`mailto:${siteContent.email}`}
              className="inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-charcoal-dark border border-dusty-rose/20 text-chalk font-medium rounded hover:bg-dusty-rose/15 transition-colors"
            >
              <HiMail className="h-4 w-4" />
              {siteContent.email}
            </a>
            <a
              href={`tel:${siteContent.phone.replace(/[^0-9]/g, '')}`}
              className="inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-charcoal-dark border border-dusty-rose/20 text-chalk font-medium rounded hover:bg-dusty-rose/15 transition-colors"
            >
              <HiPhone className="h-4 w-4" />
              {siteContent.phone}
            </a>
          </div>
        </div>
      </Section>

      {/* Final CTA */}
      <ChalkboardBackground 
        as="section" 
        backgroundImage={homeBg}
        backgroundOpacity={0.5}
        className="py-12 md:py-16 lg:py-20"
      >
        <div className="container-custom">
          <div className="text-center max-w-2xl mx-auto">
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-headline font-semibold mb-6 text-chalk uppercase tracking-wider">
              Let's Create Your Dream Look
            </h2>
            <Button
              href={siteContent.social.linktree}
              target="_blank"
              rel="noopener noreferrer"
              variant="outline"
              size="lg"
              className="!text-xs"
            >
              Check Availability
              <HiExternalLink className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </div>
      </ChalkboardBackground>
    </>
  );
}
